import {useRepositories} from "@web-libs/core/repositories/application/repository-concerns";
import {createContext, useEffect, useRef, useState} from "react";
import {AppointmentInvitation} from "@web-libs/features/appointments/domain/appointment.types";
import {useContextStrict} from "@web-libs/core/context/context.functions";
import {useInvitationProcessor} from "@web-libs/features/appointments/application/invitations/use-invitation-processor";
import {useInstitutionContext} from "@web-libs/features/institution/application/use-institution-context";
import {useDictionaryContext} from "@web-libs/core/internationalization/dictionary.context";

export type InvitationConcern = {
    invitations: AppointmentInvitation[]
    error: string|undefined
    loading: boolean
    acceptInvitation: (invitation: AppointmentInvitation) => Promise<void>
    declineInvitation: (invitation: AppointmentInvitation) => Promise<void>
    setRefresh: (refresh: () => void) => void
    selectedInvitation: AppointmentInvitation | undefined
    selectInvitation: (appointmentId?: string) => void,
    isProcessingInvitation: boolean
}

export const InvitationContext = createContext<InvitationConcern|null>(null)
InvitationContext.displayName = 'InvitationContext'

export const useInvitations = (refresh?: () => void) => {
    const invitationConcern = useContextStrict(InvitationContext)
    if(refresh) invitationConcern?.setRefresh(refresh)
    return invitationConcern
}

export const useCreateInvitationsConcern = (): InvitationConcern => {
    const {getRepo} = useRepositories()
    const appointmentRepo = getRepo('AppointmentRepository')
    const institution = useInstitutionContext()
    const [invitations, setInvitations] = useState<AppointmentInvitation[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string|undefined>(undefined)
    const refreshRef = useRef<(()=> void)|null>(null)
    const dict = useDictionaryContext()
    const refresh = () => refreshRef.current?.()

    const [selectedInvitation, setSelectedInvitation] = useState<AppointmentInvitation|undefined>(undefined)
    const selectInvitation = (invitationId?: string) => {
        if(!invitationId) return setSelectedInvitation(undefined)
        const invitation = invitations.find(i => i.appointment.id === invitationId)
        if (invitation) {
            setSelectedInvitation(invitation)
        }
    }

    const setRefresh = (refresh: () => void) => {
        refreshRef.current = refresh
    }

    useEffect(() => {
        fetchInvitations()
    }, []);

    const fetchInvitations = async () => {
        setLoading(true)
        try {
            const invitations = await appointmentRepo.getOwnInvitations(institution)
            setInvitations(invitations)
        } catch (e) {
            setError("Beim Laden der Einladungen ist ein Fehler aufgetreten.")
        } finally {
            setLoading(false)
        }
    }

    const _handleInvitationProcessSuccess = (invitation: AppointmentInvitation) => {
        const _removeInvitation = (invitation: AppointmentInvitation) => {
            setInvitations(
                invitations => invitations.filter(i => i.appointment.id !== invitation.appointment.id)
            );
        }

        _removeInvitation(invitation);
        refresh();
    }

    const {acceptInvitation, declineInvitation, isProcessingInvitation} = useInvitationProcessor(_handleInvitationProcessSuccess)

    return {
        invitations,
        loading,
        error,
        acceptInvitation,
        declineInvitation,
        setRefresh,
        selectedInvitation,
        selectInvitation,
        isProcessingInvitation
    }
}
